import { formatUnits } from "viem";

/**
 * Rendering, and only rendering.
 *
 * Every function here takes a `bigint` or a raw decimal string and returns
 * text. Nothing in this file does arithmetic that a payment depends on, which
 * is the rule that keeps `number` out of the money path — plan §60. A helper
 * that starts returning numbers for a caller to add up is the beginning of the
 * bug this file exists to prevent.
 */

export const ASQ_DECIMALS = 18;

export function toBigInt(raw: string | undefined | null): bigint {
  if (!raw) return 0n;
  try {
    return BigInt(raw);
  } catch {
    return 0n;
  }
}

/**
 * An ASQ amount for display.
 *
 * Trailing zeros are trimmed because staging amounts are whole numbers and
 * "3.000000000000000000 ASQ" is unreadable, but the value is never rounded —
 * a fractional amount keeps every digit it has. `maxFractionDigits` exists for
 * the dashboard's headline figure, where a wallet balance with eighteen decimal
 * places would break the layout; it truncates rather than rounds, so a
 * displayed balance is never larger than the real one.
 */
export function formatASQ(
  raw: string | bigint | undefined | null,
  options: { maxFractionDigits?: number } = {},
): string {
  const value = typeof raw === "bigint" ? raw : toBigInt(raw);
  const text = formatUnits(value, ASQ_DECIMALS);

  const [whole, fraction = ""] = text.split(".");
  const grouped = Number(whole).toLocaleString("en-US");

  const limit = options.maxFractionDigits;
  const kept = limit === undefined ? fraction : fraction.slice(0, limit);
  const trimmed = kept.replace(/0+$/, "");

  return trimmed ? `${grouped}.${trimmed}` : grouped;
}

/** `0x12AB…98CD`, the shape plan §9 asks for in the header. */
export function shortAddress(address: string | undefined | null): string {
  if (!address || address.length < 12) return address ?? "";
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function shortHash(hash: string | undefined | null): string {
  if (!hash || hash.length < 14) return hash ?? "";
  return `${hash.slice(0, 10)}…${hash.slice(-6)}`;
}

/**
 * Month abbreviations, fixed rather than taken from a locale.
 *
 * `toLocaleDateString` is not usable here: en-GB renders September as "Sept",
 * four characters where every other month is three, which breaks the alignment
 * of a column of dates in a ledger. Other locales disagree about more than
 * that. A fixed table is three lines and renders the same on every machine.
 */
const MONTHS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
] as const;

/**
 * `08 SEP 2026` — the dense uppercase date the ledger and timeline use.
 *
 * Rendered in the reader's own timezone, deliberately. These are expiry dates
 * and payment times, and "when does my membership run out" is a question about
 * the reader's calendar rather than about UTC.
 */
export function formatDate(value: string | Date | undefined | null): string {
  const date = asDate(value);
  if (!date) return "—";

  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatDateTime(value: string | Date | undefined | null): string {
  const date = asDate(value);
  if (!date) return "—";

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${formatDate(date)}  ${hours}:${minutes}`;
}

function asDate(value: string | Date | undefined | null): Date | null {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Days from now until `value`, floored at 0. Negative would read as a term. */
export function daysUntil(value: string | undefined | null): number {
  if (!value) return 0;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return 0;
  return Math.max(0, Math.ceil((target - Date.now()) / 86_400_000));
}

/**
 * The first six hex characters of a registry id, as a coordinate label.
 *
 * Used as the annotation on utility panels. It is real data rather than a
 * decorative `01 / 02 / 03`: two utilities can never share it, and it is
 * checkable against the chain by eye.
 */
export function idFragment(id: string | undefined | null): string {
  if (!id) return "——————";
  return id.replace(/^0x/, "").slice(0, 6).toUpperCase();
}
